import React from 'react'
import clsx from 'clsx'
import { FormattedMessage, useIntl } from 'react-intl'
import { Link as MuiLink, Grid, Tooltip } from '@material-ui/core'
import { makeStyles, Theme } from '@material-ui/core/styles'
import ClearIcon from '@material-ui/icons/Clear'
import RemoveIcon from '@material-ui/icons/Remove'
import EditIcon from '@src/icons/EditIcon'
import styles from '@src/routes/Configurators/utils/styles'
import { priceFormatter } from '@src/routes/Configurators/utils/PriceFormatter'
import messages from '../../../messages'
import SubtotalAndAddToCart from './SubtotalAndAddToCart'
import { NgsoType, Modifications, NGSOTypeOptions } from '../enum'
import ConfirmRemovalPopup from '../../../components/ConfirmRemovalPopup'
import EditOligoName from './EditOligoName'
import { checkValidKey } from '@src/routes/Configurators/utils/util'
import EditConcentration from './EditConcentration'

const useStyles = makeStyles((theme: Theme) => {
  return {
    container: {
      width: '100%',
    },
    gridHead: {
      backgroundColor: theme.palette.background.grey,
    },
    gridRow: {
      borderTop: `1px solid ${theme.palette.grey[300]}`,
      padding: theme.spacing(3),
    },
    gridItem: {
      '&:nth-child(1)': {
        width: '20%',
        overflowWrap: 'break-word',
        paddingRight: theme.spacing(2),
      },
      '&:nth-child(2)': { width: '35%' },
      '&:nth-child(3)': { width: '15%' },
      '&:nth-child(4)': { width: '15%' },
      '&:nth-child(5)': { width: '15%', textAlign: 'right' },
    },
    price: {
      paddingRight: theme.spacing(10),
    },
    list: {
      margin: 0,
      paddingLeft: 0,
    },
    label: {
      fontWeight: theme.typography.fontWeightBold,
      fontSize: theme.typography.pxToRem(12),
      textTransform: 'capitalize',
    },
    shippingLabel: {
      fontSize: theme.typography.pxToRem(14),
    },
    link: {
      color: theme.palette.primary.main,
      fontWeight: theme.typography.fontWeightBold,
    },
    heading: {
      marginTop: theme.spacing(5),
      marginBottom: theme.spacing(3),
      fontSize: theme.typography.pxToRem(18),
      fontWeight: theme.typography.fontWeightBold,
    },
    subHeading: {
      fontWeight: theme.typography.fontWeightRegular,
    },
    deleteAll: {
      color: theme.palette.primary.main,
      fontWeight: theme.typography.fontWeightBold,
      fontSize: theme.typography.pxToRem(14),
    },
    sectionTitle: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
    },
    editIcon: {
      fontSize: theme.typography.pxToRem(14),
    },
    priceValue: {
      marginRight: theme.spacing(5),
    },
    action: {
      display: 'flex',
      justifyContent: 'flex-end',
      alignItems: 'center',
    },
    sequence: {
      paddingRight: theme.typography.pxToRem(50),
    },
    arrow: {
      color: theme.palette.common.white,
    },
    tooltip: {
      maxWidth: theme.typography.pxToRem(600),
      backgroundColor: theme.palette.common.white,
      fontSize: theme.typography.pxToRem(12),
      color: '#151515',
      boxShadow: theme.shadows[5],
      display: 'table',
      wordBreak: 'break-all',
      marginTop: theme.spacing(0.5),
      borderRadius: theme.typography.pxToRem(3),
      padding: theme.spacing(8),
    },
    sequenceLabel: {
      fontSize: theme.typography.pxToRem(20),
      fontWeight: theme.typography.fontWeightBold,
      paddingBottom: theme.spacing(8),
      textTransform: 'uppercase',
    },
    sequenceInfo: {
      fontWeight: 'normal',
      fontSize: theme.typography.pxToRem(14),
    },
    sequenceText: {
      fontWeight: theme.typography.fontWeightBold,
    },
    cursorPointer: {
      cursor: 'pointer',
    },
    editOligo: {
      cursor: 'pointer',
    },
    fontBoldHeading: {
      fontWeight: theme.typography.fontWeightMedium,
      width: theme.spacing(30),
      fontSize: theme.typography.pxToRem(14),
    },
    fontBoldContent: {
      fontWeight: theme.typography.fontWeightMedium,
      alignContent: 'left',
      fontSize: theme.typography.pxToRem(14),
    },
    formatMargin: {
      marginBottom: theme.spacing(4),
    },
  }
})

export const OrderDetails = (props) => {
  const classes = useStyles()
  const GlobalCSS = styles()
  const intl = useIntl()
  const [dialogOpen, setDialogOpen] = React.useState(false)
  const [concentrationDialogOpen, setConcentrationDialogOpen] =
    React.useState(false)
  const [selectedName, setSelectedName] = React.useState()
  const [selectedConcentration, setSelectedConcentration] = React.useState()
  const [selectedIndex, setSelectedIndex] = React.useState()
  const {
    sessionData,
    productDetails,
    saveSessionHandler,
    productInformation,
    clearSession,
    totalValidation,
    configuratorAddToMiniCart,
  } = props
  const [delDialogOpen, setDelDialogOpen] = React.useState(false)
  const [ngsoToEmpty, setNgsoToEmpty] = React.useState(false)
  const [delItem, setDelItem] = React.useState()
  const [delType, setDelType] = React.useState('')
  const [subTotalPrice, setSubTotalPrice] = React.useState<string>('--')
  const orderItems = props.sessionData[0]?.wellList

  React.useEffect(() => {
    let subtotal = 0
    let quoteItem = 0

    // Calculating the price of total number of Oligos in session
    sessionData.forEach((plate) => {
      plate.wellList.forEach((well) => {
        if (well.price === Modifications.priceQuote) {
          quoteItem = quoteItem + 1
        }
      })
    })
    if (quoteItem !== 0) {
      setSubTotalPrice(intl.formatMessage(messages._PC_QUOTE_ITEM))
    } else {
      sessionData.forEach((plate) => {
        plate.wellList.forEach((well) => {
          subtotal = subtotal + parseFloat(well.price)
        })
      })
      if (subtotal) {
        const formattedTotalPrice = priceFormatter(
          subtotal,
          productInformation.currencyFormat,
          productInformation.thousandSeparator,
          productInformation.thousandSpacing,
          productInformation.decimalSeparator,
          productInformation.prefixCurrecny,
          sessionData[0]?.wellList[0]?.currencySymbol
        )
        if (formattedTotalPrice !== Modifications.NaN) {
          setSubTotalPrice(formattedTotalPrice)
        } else {
          setSubTotalPrice('--')
        }
      } else {
        setSubTotalPrice('--')
      }
    }
    // TODO: need a test before we refactor these dependencies
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionData])
  const deleteSelectedItem = (item) => {
    if (
      sessionData[0].wellList[0].ngsoType === 'NGSO2' &&
      sessionData[0].wellList.length <= 12
    ) {
      setNgsoToEmpty(true)
      setDelItem(item)
      setDelType('Selected')
      setDelDialogOpen(true)
    } else {
      setDelItem(item)
      setDelType('Selected')
      setDelDialogOpen(true)
      setNgsoToEmpty(false)
    }
  }
  const deleteOligoItem = (item, type) => {
    if (type === 'Selected' && sessionData[0].wellList.length !== 1) {
      const index = sessionData[0].wellList.indexOf(item)
      if (index !== -1) {
        sessionData[0].wellList.splice(index, 1)
      }
      totalValidation(sessionData, true)
    } else if (sessionData[0].wellList.length === 1 || type === 'All') {
      clearSession(true)
    }
  }

  const deleteAllHandler = () => {
    setDelItem(sessionData)
    setDelType('All')
    setDelDialogOpen(true)
    setNgsoToEmpty(false)
  }
  const handleOligoname = (item, index) => {
    setDialogOpen(true)
    setSelectedName(item)
    setSelectedIndex(index)
  }
  const UpdateNewName = (newname, index) => {
    sessionData[0].wellList[index].name = newname
    props.saveSessionHandler(sessionData, false)
  }

  const editConcentartion = (seletedConcentration) => {
    setSelectedConcentration(seletedConcentration)
    setConcentrationDialogOpen(true)
  }

  const UpdateNewConcentration = (newConcentration) => {
    sessionData.forEach((data) => {
      data.wellList.forEach((item) => {
        item.concentration = newConcentration
      })
    })
    saveSessionHandler(sessionData, false)
  }

  return (
    <div className={classes.container}>
      <div className={clsx(classes.heading, classes.sectionTitle)}>
        <div>
          <FormattedMessage {...messages.ORDER_DETAILS} />
          &nbsp;
          <span className={classes.subHeading}>
            ({orderItems.length} Oligos)
          </span>
        </div>
        <div className={clsx(classes.deleteAll, classes.cursorPointer)}>
          <MuiLink color="primary" onClick={() => deleteAllHandler()}>
            <FormattedMessage {...messages.CONF_DELETE_ALL_BUTTON} />
          </MuiLink>
        </div>
      </div>
      <Grid container className={classes.formatMargin}>
        <Grid item className={classes.fontBoldHeading}>
          <FormattedMessage {...messages.CONF_FORMAT} />
        </Grid>
        <Grid item className={classes.fontBoldContent}>
          {sessionData[0]?.wellList[0]?.format ? (
            <FormattedMessage
              {...checkValidKey(
                messages[`${sessionData[0]?.wellList[0]?.format}`],
                `${sessionData[0]?.wellList[0]?.format}`
              )}
            />
          ) : (
            '--'
          )}
        </Grid>
      </Grid>
      {sessionData[0].wellList[0].format !== Modifications.Dry ? (
        <Grid container className={classes.formatMargin}>
          <Grid item className={classes.fontBoldHeading}>
            <FormattedMessage {...messages._PC_SPEC_CONCENTRATION} />
          </Grid>

          <Grid item className={classes.fontBoldContent}>
            <span>
              {sessionData[0]?.wellList[0]?.concentration
                ? sessionData[0]?.wellList[0]?.concentration
                : '--'}
            </span>{' '}
            &nbsp;
            {productDetails.isCSR &&
            sessionData[0]?.wellList[0]?.ngsoType ===
              NGSOTypeOptions.NGSOGold ? (
              <MuiLink
                onClick={() =>
                  editConcentartion(sessionData[0]?.wellList[0]?.concentration)
                }
                className={classes.editOligo}
              >
                {' '}
                <EditIcon className={classes.editIcon} />
              </MuiLink>
            ) : (
              ''
            )}
          </Grid>
        </Grid>
      ) : (
        ''
      )}
      <Grid
        container
        direction="row"
        className={clsx(classes.gridRow, classes.gridHead)}
      >
        <Grid item className={clsx(classes.gridItem, classes.label)}>
          <FormattedMessage {...messages.CONF_NGSO_OLIGO_LABEL} />
        </Grid>
        <Grid item className={clsx(classes.gridItem, classes.label)}>
          <FormattedMessage {...messages.CONF_SEQUENCE_TEXT} />
        </Grid>
        <Grid item className={clsx(classes.gridItem, classes.label)}>
          <FormattedMessage {...messages.CONF_SCALE_TEXT} />
        </Grid>
        <Grid item className={clsx(classes.gridItem, classes.label)}>
          <FormattedMessage {...messages.CONF_OPTIONS_TEXT} />
        </Grid>
        <Grid
          item
          className={clsx(classes.gridItem, classes.label, classes.price)}
        >
          <FormattedMessage {...messages.PRICE} />
        </Grid>
      </Grid>
      {orderItems?.map((item, index) => {
        const sequence = [
          item.fiveMod && item.fiveMod !== Modifications.None
            ? item.fiveMod
            : '',
          item.sequence,
          item.threeMod && item.threeMod !== Modifications.None
            ? item.threeMod
            : '',
        ]
          .filter(Boolean)
          .join('')
        return (
          <Grid
            container
            direction="row"
            className={classes.gridRow}
            key={index}
          >
            <Grid item className={clsx(classes.gridItem, classes.link)}>
              <MuiLink
                onClick={() => handleOligoname(item.name, index)}
                className={classes.editOligo}
              >
                <span>{item.name ? item.name : '--'}</span> &nbsp;
                <EditIcon className={classes.editIcon} />
              </MuiLink>
            </Grid>
            <Grid
              item
              className={clsx(classes.gridItem, classes.link, classes.sequence)}
            >
              <Tooltip
                title={
                  <>
                    <div className={classes.sequenceLabel}>
                      <FormattedMessage {...messages.CONF_SEQUENCE_LABEL} />
                    </div>
                    <div className={classes.sequenceInfo}>
                      {item.sequence ? sequence : '--'}
                    </div>
                  </>
                }
                interactive
                classes={{
                  tooltip: classes.tooltip,
                  arrow: classes.arrow,
                }}
                placement="bottom-start"
              >
                <MuiLink
                  color="primary"
                  className={clsx(
                    GlobalCSS.seqWordWrapwithDot,
                    classes.sequenceText
                  )}
                >
                  {item.sequence ? sequence : '--'}
                </MuiLink>
              </Tooltip>
            </Grid>
            <Grid item className={classes.gridItem}>
              {item.scale ? (
                <>
                  {parseFloat(item.scale).toFixed(2).replace(/[0]$/, '')}{' '}
                  <FormattedMessage
                    {...messages.CONF_DNAPLATES_SCALE_MEASURE}
                  />
                </>
              ) : (
                '--'
              )}
            </Grid>
            <Grid item className={classes.gridItem}>
              {item.ngsoType ? (
                <span>{NgsoType[item.ngsoType]}</span>
              ) : (
                <RemoveIcon />
              )}
            </Grid>
            <Grid item className={clsx(classes.gridItem, classes.action)}>
              {item.price === 'QUOTE' ? (
                <span className={classes.priceValue}>
                  <FormattedMessage {...messages.QUOTE_ITEM} />
                </span>
              ) : (
                <span className={classes.priceValue}>
                  {item.priceHeaderError
                    ? '--'
                    : item.price
                      ? priceFormatter(
                          item.price,
                          productInformation.currencyFormat,
                          productInformation.thousandSeparator,
                          productInformation.thousandSpacing,
                          productInformation.decimalSeparator,
                          productInformation.prefixCurrecny,
                          item.currencySymbol
                        )
                      : '--'}
                </span>
              )}
              <span className={classes.cursorPointer}>
                <ClearIcon onClick={() => deleteSelectedItem(item)} />
              </span>
            </Grid>
          </Grid>
        )
      })}

      {delDialogOpen && (
        <ConfirmRemovalPopup
          dialogOpen={delDialogOpen}
          setDialogOpen={setDelDialogOpen}
          selectedItem={delItem}
          onDelete={deleteOligoItem}
          type={delType}
          confirmRemoval={ngsoToEmpty}
          productType={productDetails.type}
        />
      )}
      <SubtotalAndAddToCart
        handlePrev={props.handlePrev}
        totalPrice={subTotalPrice}
        shipping={props.product?.shipping}
        additionalServices={props.additionalServices}
        productDetails={productDetails}
        saveSessionHandler={saveSessionHandler}
        configuratorAddToMiniCart={configuratorAddToMiniCart}
        clearSession={clearSession}
      />
      {dialogOpen ? (
        <EditOligoName
          dialogOpen={dialogOpen}
          title={<FormattedMessage {...messages.CONF_NGSO_EDIT_OLIGO_NAME} />}
          fieldLabel={<FormattedMessage {...messages.CONF_OLIGO_NAME_NGSO} />}
          setDialogOpen={setDialogOpen}
          selectedName={selectedName}
          selectedIndex={selectedIndex}
          UpdateNewName={UpdateNewName}
        />
      ) : (
        ''
      )}
      {concentrationDialogOpen ? (
        <EditConcentration
          dialogOpen={concentrationDialogOpen}
          setDialogOpen={setConcentrationDialogOpen}
          selectedConcentration={selectedConcentration}
          UpdateNewConcentration={UpdateNewConcentration}
        />
      ) : (
        ''
      )}
    </div>
  )
}

export default OrderDetails
